// The ⋯ menu in a post's top-right corner, ported from keep_or_mull's
// PostMenu: a dots button that drops a small menu, whose one item today is
// "Report post". Reporting opens a modal — category, optional explanation —
// that posts to ModerationReportsController.
//
// The menu closes on an outside click, on Escape, and on picking an item,
// the same three ways keep_or_mull's did.
import { type JSX, useEffect, useRef, useState } from "react"
import { reportPost, type ReportReason } from "./api"

export function PostMenu({
  postsUrl,
  postId,
  signedIn,
  reasons,
}: {
  postsUrl: string
  postId: string
  signedIn: boolean
  reasons: ReportReason[]
}): JSX.Element {
  const [open, setOpen] = useState(false)
  const [reporting, setReporting] = useState(false)
  const container = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return

    function onPointerDown(event: MouseEvent): void {
      if (!container.current?.contains(event.target as Node)) setOpen(false)
    }
    function onKeyDown(event: KeyboardEvent): void {
      if (event.key === "Escape") setOpen(false)
    }

    document.addEventListener("mousedown", onPointerDown)
    document.addEventListener("keydown", onKeyDown)
    return () => {
      document.removeEventListener("mousedown", onPointerDown)
      document.removeEventListener("keydown", onKeyDown)
    }
  }, [open])

  return (
    <div className="discuss__post-menu" ref={container}>
      <button
        type="button"
        className="discuss__post-menu-toggle"
        aria-label="Post options"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        <svg viewBox="0 0 16 16" width="16" height="16" fill="currentColor" aria-hidden="true">
          <circle cx="3" cy="8" r="1.5" />
          <circle cx="8" cy="8" r="1.5" />
          <circle cx="13" cy="8" r="1.5" />
        </svg>
      </button>

      {open && (
        <div className="discuss__post-menu-list" role="menu">
          {signedIn ? (
            <button
              type="button"
              role="menuitem"
              className="discuss__post-menu-item"
              onClick={() => {
                setOpen(false)
                setReporting(true)
              }}
            >
              Report post
            </button>
          ) : (
            <a role="menuitem" className="discuss__post-menu-item" href="/signin">
              Sign in to report
            </a>
          )}
        </div>
      )}

      <ReportModal
        open={reporting}
        postsUrl={postsUrl}
        postId={postId}
        reasons={reasons}
        onClose={() => setReporting(false)}
      />
    </div>
  )
}

// Rendered on the project's native <dialog> chrome (modal.scss), so Escape,
// the focus trap and the backdrop come from the browser.
function ReportModal({
  open,
  postsUrl,
  postId,
  reasons,
  onClose,
}: {
  open: boolean
  postsUrl: string
  postId: string
  reasons: ReportReason[]
  onClose: () => void
}): JSX.Element {
  const dialog = useRef<HTMLDialogElement>(null)
  const [category, setCategory] = useState("")
  const [explanation, setExplanation] = useState("")
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [sent, setSent] = useState(false)

  useEffect(() => {
    const element = dialog.current
    if (!element) return
    if (open && !element.open) {
      // Fresh every time it opens — a previous report's category shouldn't
      // be pre-filled on the next post.
      setCategory("")
      setExplanation("")
      setError(null)
      setSent(false)
      element.showModal()
    } else if (!open && element.open) {
      element.close()
    }
  }, [open])

  async function submit(): Promise<void> {
    if (!category || sending) return
    setSending(true)
    setError(null)
    const result = await reportPost(postsUrl, postId, {
      reason_category: category,
      explanation: explanation.trim() || undefined,
    })
    setSending(false)

    if (typeof result === "string") {
      setError(result)
      return
    }
    setSent(true)
  }

  return (
    <dialog
      ref={dialog}
      className="modal discuss-report-modal"
      onClose={onClose}
      onClick={(event) => {
        // A click on the backdrop (the dialog element itself) closes.
        if (event.target === dialog.current) onClose()
      }}
    >
      <div className="modal__panel">
        <div className="modal__head">
          <h2 className="modal__title">{sent ? "Report received" : "Report this post"}</h2>
          <button type="button" className="modal__close" aria-label="Close" onClick={onClose}>
            ×
          </button>
        </div>

        {sent ? (
          <div className="discuss-report">
            <p className="discuss-report__note">
              Thanks — someone will take a look. The post stays up until then.
            </p>
            <div className="discuss-report__actions">
              <button type="button" className="btn btn--primary" onClick={onClose}>
                Done
              </button>
            </div>
          </div>
        ) : (
          <form
            className="discuss-report"
            onSubmit={(event) => {
              event.preventDefault()
              void submit()
            }}
          >
            {error && <p className="discuss__problem" role="alert">{error}</p>}

            <label className="discuss-report__label" htmlFor={`report-reason-${postId}`}>
              What's wrong with it?
            </label>
            <select
              id={`report-reason-${postId}`}
              className="discuss__input discuss-report__select"
              value={category}
              onChange={(event) => setCategory(event.target.value)}
            >
              <option value="">Pick a reason…</option>
              {reasons.map((reason) => (
                <option key={reason.value} value={reason.value}>
                  {reason.label}
                </option>
              ))}
            </select>

            <label className="discuss-report__label" htmlFor={`report-explanation-${postId}`}>
              Explain your report
            </label>
            <textarea
              id={`report-explanation-${postId}`}
              className="discuss__input discuss__input--area"
              rows={3}
              maxLength={2000}
              value={explanation}
              onChange={(event) => setExplanation(event.target.value)}
            />

            <div className="discuss-report__actions">
              <button type="button" className="btn" onClick={onClose}>
                Cancel
              </button>
              <button type="submit" className="btn btn--primary" disabled={sending || !category}>
                {sending ? "Sending…" : "Submit report"}
              </button>
            </div>
          </form>
        )}
      </div>
    </dialog>
  )
}
